/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import type { HostContext, Logger, ResultItem, RootState } from '../../../@types';
import SearchDropdownFile from './SearchDropdownFile';
import HorizontalRuler from '../../../components/HorizontalRuler';
import {
	setExactTypedSearchQuery,
	setSRPCurrentTab,
	setSearchQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
} from '../../../actions/searchActions';
import SearchUtils from '../../../utils/SearchUtils';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchService from '../../../services/SearchService';
import SearchConfig from '../../../utils/SearchConfig';

import './SearchDropdownFile.scss';

type OwnProps = {
	logger: Logger;
	onSubmit: (...args: any[]) => void;
	hostShowSearchDropdown: boolean;
	variant: 'presearch' | 'autocomplete';
	routeToFiles?: boolean;
};

type SearchDropdownFilesProps = OwnProps & WrappedComponentProps & PropsFromRedux;
/*
 * This renders the Apps section in the Search Dropdown.
 */
class SearchDropdownFiles extends Component<SearchDropdownFilesProps> {
	analytics: SearchAnalytics;
	searchHostContext: HostContext;
	recentsFileLimit: number;
	filesAutoCompleteLimit: number;

	constructor(props) {
		super(props);
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		const env = this.searchHostContext.environment;
		const recentFilesConfig: any = props.searchConfig?.ui?.recentFiles?.[env] ?? {};
		this.recentsFileLimit = recentFilesConfig?.limit ?? 6;
		const filesAutoCompleteConfig: any = props.searchConfig?.ui?.filesAutoComplete?.[env] ?? {};
		this.filesAutoCompleteLimit = filesAutoCompleteConfig?.limit ?? 6;
		this.allFilesClickHandler = this.allFilesClickHandler.bind(this);
	}

	allFilesClickHandler(): void {
		const tabs = SearchConfig.fetchSRPTabs();
		if (this.props.variant === 'autocomplete' && tabs?.length) {
			this.executeFileSearch();
			const filesTabIndex = tabs.indexOf('yourWork');
			filesTabIndex >= 0 && this.props.setSRPCurrentTab(filesTabIndex);
		}
	}

	async executeFileSearch() {
		const searchHostContext = SearchHostContext.getContext();
		const trimmedSearchQuery = this.props.saytFiles.query.trim();
		SearchAnalytics.searchType = SearchAnalytics.autoComplete;
		const analytics = new SearchAnalytics(searchHostContext);
		analytics.initSearchAnalytics(trimmedSearchQuery);
		analytics.postDropDownSelection(0, trimmedSearchQuery);
		this.props.setTempSearchQuery('');
		this.props.setSearchQuery(trimmedSearchQuery);
		this.props.setExactTypedSearchQuery(trimmedSearchQuery);
		this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
		this.props.setSubmittedSearchQuery(true);
		// Save to local storage in case the refresh the results page
		this.props.onSubmit(trimmedSearchQuery);
		this.props.logger.info('Files SAYT', 'Logged Search Query', trimmedSearchQuery);
		SearchUtils.setSearchHistoryWithSearchQuery(this.props.searchHistoryItems, trimmedSearchQuery);

		try {
			if (
				searchHostContext.featureFlags.includes('temp_enable_search_history_server') &&
				trimmedSearchQuery !== ''
			) {
				const searchService = new SearchService(searchHostContext);
				await searchService.pushSearchHistory(trimmedSearchQuery);
			}
		} catch (e) {
			this.props.logger.error(
				'Files SAYT',
				'Problem creating/updating search history to search server on search auto suggestion click',
				e,
			);
		}
	}

	renderFiles(files: ResultItem[], variant: 'presearch' | 'autocomplete', query?: string) {
		this.analytics.initSearchAnalytics(query);
		const fileLimit = variant === 'presearch' ? this.recentsFileLimit : this.filesAutoCompleteLimit;
		const filesMap: Record<string, ResultItem> = files?.reduce((acc, file: ResultItem) => {
			acc[file.id] = acc[file.id] || file;
			return acc;
		}, {});
		const titleString =
			variant === 'presearch' ? 'app.search.presearch.recentFiles' : 'app.search.autocomplete.files';
		return (
			<div className='search-dropdown-recentfile-group' role='group'>
				<div className='search-dropdown-recentfile-group-title'>
					<span className='search-dropdown-recentfile-group-title-leftheader'>
						<FormattedMessage id={titleString} />
					</span>
					{this.props.routeToFiles && variant === 'autocomplete' && (
						<span
							className='search-dropdown-recentfile-group-title-rightheader'
							onKeyDown={(event) => SearchUtils.onKeyDown(event)}
							onClick={this.allFilesClickHandler}
							tabIndex={0}
							role='link'
						>
							<a role='navigation'>
								<span className='all-text'>
									<FormattedMessage id='app.search.showallyourfiles' />
								</span>
							</a>
						</span>
					)}
				</div>

				{Object.values(filesMap)
					.slice(0, fileLimit)
					?.map((file: ResultItem, index: number) => (
						<SearchDropdownFile
							key={file.id}
							file={file}
							index={index}
							logger={this.props.logger}
							variant={variant}
						/>
					))}
				<HorizontalRuler />
			</div>
		);
	}

	render() {
		const { saytFiles, variant } = this.props;

		if (variant === 'presearch' && saytFiles.recent.length) {
			return this.renderFiles(saytFiles.recent, variant);
		}

		if (variant === 'autocomplete' && saytFiles.matched.length) {
			return this.renderFiles(saytFiles.matched, variant, saytFiles.query);
		}

		return null;
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	saytFiles: search.searchAutoCompleteFiles,
	searchConfig: search.searchConfig,
	searchHistoryItems: search.searchHistoryItems,
});

const mapDispatchToProps = {
	setSRPCurrentTab,
	setExactTypedSearchQuery,
	setSearchQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropdownFiles));
